export const toDateOnly = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

export const addDays = (date: Date, days: number): Date => {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
}

export const isSameDate = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

export const formatDayLabel = (date: Date): string =>
    date.toLocaleDateString([], { weekday: "short", month: "numeric", day: "numeric" })

export const toDateInputValue = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
}

export const fromDateInputValue = (value: string): Date => {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day)
}

export const getUpcomingWeekendDate = (): Date => {
    const today = toDateOnly(new Date())
    const dayOfWeek = today.getDay()
    if (dayOfWeek === 0 || dayOfWeek === 6)
        return today
    return addDays(today, 6 - dayOfWeek)
}

export interface DayPickOption {
    label: string
    date: Date
}

// Most floaters plan for the coming weekend, so quick picks favor Today/Tomorrow/Sat/Sun,
// with duplicates (e.g. "Today" already being Saturday) collapsed to a single chip.
export const getQuickPickDates = (): DayPickOption[] => {
    const today = toDateOnly(new Date())
    const dayOfWeek = today.getDay()
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7
    const saturday = addDays(today, daysUntilSaturday)
    const sunday = addDays(saturday, 1)

    const candidates: DayPickOption[] = [
        { label: "Today", date: today },
        { label: "Tomorrow", date: addDays(today, 1) },
        { label: formatDayLabel(saturday), date: saturday },
        { label: formatDayLabel(sunday), date: sunday },
    ]

    const seenDates = new Set<string>()
    return candidates.filter(({ date }) => {
        const key = toDateInputValue(date)
        if (seenDates.has(key))
            return false
        seenDates.add(key)
        return true
    })
}

export type DateRelation = "past" | "today" | "future"

export const getDateRelation = (date: Date): DateRelation => {
    const todayTime = toDateOnly(new Date()).getTime()
    const dateTime = toDateOnly(date).getTime()

    if (dateTime === todayTime)
        return "today"
    return dateTime > todayTime ? "future" : "past"
}
